import React, { useState, useEffect, useReducer } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Typography,
    Chip
} from "@material-ui/core";
import {
    Edit as EditIcon,
    DeleteOutline as DeleteIcon,
    Link as LinkIcon,
    Add as AddIcon
} from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import CampaignLandingPageModal from "../../components/CampaignLandingPageModal";
import * as campaignLandingPagesService from "../../services/campaignLandingPages";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    customTableCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_LANDING_PAGES") {
        const landingPages = action.payload;
        return [...landingPages];
    }

    if (action.type === "UPDATE_LANDING_PAGE") {
        const landingPage = action.payload;
        const landingPageIndex = state.findIndex((lp) => lp.id === landingPage.id);

        if (landingPageIndex !== -1) {
            state[landingPageIndex] = landingPage;
            return [...state];
        } else {
            return [landingPage, ...state];
        }
    }

    if (action.type === "DELETE_LANDING_PAGE") {
        const landingPageId = action.payload;
        return state.filter((lp) => lp.id !== landingPageId);
    }

    if (action.type === "RESET") {
        return [];
    }
};

const CampaignLandingPages = () => {
    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const [landingPages, dispatch] = useReducer(reducer, []);
    const [selectedLandingPage, setSelectedLandingPage] = useState(null);
    const [landingPageModalOpen, setLandingPageModalOpen] = useState(false);
    const [deletingLandingPage, setDeletingLandingPage] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const landingPages = await campaignLandingPagesService.getAll();
                dispatch({ type: "LOAD_LANDING_PAGES", payload: landingPages || [] });
            } catch (err) {
                toast.error("Erro ao carregar landing pages");
            }
            setLoading(false);
        })();
    }, []);

    const handleOpenLandingPageModal = () => {
        setSelectedLandingPage(null);
        setLandingPageModalOpen(true);
    };

    const handleCloseLandingPageModal = () => {
        setSelectedLandingPage(null);
        setLandingPageModalOpen(false);
    };

    const handleEditLandingPage = (landingPage) => {
        setSelectedLandingPage(landingPage);
        setLandingPageModalOpen(true);
    };

    const handleDeleteLandingPage = async (landingPageId) => {
        try {
            await campaignLandingPagesService.remove(landingPageId);
            toast.success("Landing page deletada com sucesso");
            dispatch({ type: "DELETE_LANDING_PAGE", payload: landingPageId });
        } catch (err) {
            toast.error("Erro ao deletar landing page");
        }
        setDeletingLandingPage(null);
    };

    const handleCopyLink = (slug) => {
        const url = `${window.location.origin}/lp/${slug}`;
        navigator.clipboard.writeText(url);
        toast.success("Link copiado para a área de transferência!");
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title="Deletar Landing Page"
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={() => handleDeleteLandingPage(deletingLandingPage.id)}
            >
                Tem certeza que deseja deletar esta landing page?
            </ConfirmationModal>
            <CampaignLandingPageModal
                open={landingPageModalOpen}
                onClose={handleCloseLandingPageModal}
                landingPageId={selectedLandingPage?.id}
                onSave={(landingPage) => {
                    dispatch({ type: "UPDATE_LANDING_PAGE", payload: landingPage });
                    handleCloseLandingPageModal();
                }}
            />
            <MainHeader>
                <Title>Landing Pages</Title>
                <MainHeaderButtonsWrapper>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenLandingPageModal}
                        startIcon={<AddIcon />}
                    >
                        Nova Landing Page
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">Nome</TableCell>
                            <TableCell align="center">Slug</TableCell>
                            <TableCell align="center">Título</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {landingPages.map((landingPage) => (
                            <TableRow key={landingPage.id}>
                                <TableCell align="center">{landingPage.name}</TableCell>
                                <TableCell align="center">{landingPage.slug}</TableCell>
                                <TableCell align="center">{landingPage.title}</TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={landingPage.isActive ? "Ativa" : "Inativa"}
                                        color={landingPage.isActive ? "primary" : "default"}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Copiar Link">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopyLink(landingPage.slug)}
                                        >
                                            <LinkIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Editar">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditLandingPage(landingPage)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Deletar">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setDeletingLandingPage(landingPage);
                                                setConfirmModalOpen(true);
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </MainContainer>
    );
};

export default CampaignLandingPages;
